/**
 * Mock data for the Lighthouse Review page.
 * Used by LighthouseApi.getReview() in dev mode to render a realistic OPF review.
 */
import {LighthouseReviewData} from 'src/app/api/model/response/LighthouseReviewResponse';
import {FieldType, ReviewStatus, ReviewType} from 'src/app/constant/Enums';

export const MOCK_LIGHTHOUSE_REVIEW_DATA: LighthouseReviewData = {
    reviewId: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    referenceId: 'A1X1BOUT4WLDV3',
    referenceSubType: 'DP',
    referenceType: 'TRANSPORTER',
    caseType: 'BWND',
    caseSubType: 'BWND',
    businessType: 'AMZL',
    reviewType: ReviewType.PRIMARY_AUDIT,
    reviewStatus: ReviewStatus.PENDING,
    reviewResult: '',
    country: 'US',
    reOpenFor: null,
    creationDate: 1719828000000,
    lastModifiedBy: null,
    lastModifiedDate: 1719828000000,
    lastReviewDecisionDetail: null,

    caseDetails: [
        {label: 'Transporter ID', value: 'A252AB3GLGSDBQ (ACTIVE)', type: FieldType.LINK, href: 'https://logistics.amazon.com/amconsole/transporter/A252AB3GLGSDBQ'},
        {label: 'Case Creation Date', value: '2026-07-21 12:59:07 UTC', type: FieldType.TEXT},
        {label: 'Case Status', value: 'PENDING', type: FieldType.TEXT},
        {label: 'Investigation Date', value: 'N/A', type: FieldType.TEXT},
        {label: 'Sub use case', value: 'OPF', type: FieldType.TEXT},
        {label: 'Human in loop decision', value: 'PENDING', type: FieldType.TEXT},
        {label: 'Action Result', value: 'N/A', type: FieldType.TEXT},
        {label: 'Blurb Sent to the DP', value: 'N/A', type: FieldType.TEXT}
    ],

    auditChecks: [
        {label: 'Is this a duplicate ticket?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Has this DP been previously reinstated?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: "Are there applicable SYSKA's on DPs account?", value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is the DP listed on the DND List?', value: 'No', type: FieldType.TEXT, editable: false},
        {label: 'Does the DP have an active High-Severity ticket?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: "Is the DP impacted by LSE's?", value: 'No', type: FieldType.TEXT, editable: false},
        {label: "Number of LSE's", value: '0', type: FieldType.NUMBER, editable: false},
        {label: 'Number of Blocks Forgiven', value: '0', type: FieldType.NUMBER, editable: false},
        {label: "Applicable LSE's", value: null, type: FieldType.DROPDOWN, options: ['None', 'LSE-2026-001', 'LSE-2026-002', 'LSE-2026-003'], editable: true},
        {label: 'Exemption reason', value: null, type: FieldType.FREEFORM, editable: true},
        {label: 'New Block total in 90 days Lookback', value: '39', type: FieldType.NUMBER, editable: false},
        {label: 'New BWND total in 90 days Lookback', value: '24', type: FieldType.NUMBER, editable: false},
        {label: 'New Threshold Abuse 90 days Lookback BWND rate', value: '61.54 %', type: FieldType.TEXT, editable: false},
        {label: 'Avg check-in to DL scan time', value: '13.67 mins', type: FieldType.TEXT, editable: false},
        {label: 'Avg station wave BWND', value: '53.60 %', type: FieldType.TEXT, editable: false},
        {label: 'Avg block start to swipe to arrive', value: '-4.38', type: FieldType.NUMBER, editable: false},
        {label: 'Number of alerts sent', value: '2', type: FieldType.NUMBER, editable: false},
        {label: 'Last alert sent', value: '2026-07-19 12:45:24.86', type: FieldType.TEXT, editable: false},
        {label: 'No of defect post last alert', value: '4', type: FieldType.NUMBER, editable: false},
        {label: 'Business Date', value: '2026-07-20T18:00:00.000Z', type: FieldType.TEXT, editable: false},
        {label: 'Is Offboarding ticket filled by authorized person?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is DP still active?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true},
        {label: 'Is the Offboarding ticket Fraud-related?', value: null, type: FieldType.RADIO, options: ['Yes', 'No'], editable: true, required: true}
    ],

    dataPoints: [
        {label: 'Transporter ID', value: 'A252AB3GLGSDBQ', type: FieldType.TEXT},
        {label: 'Country', value: 'US', type: FieldType.TEXT},
        {label: 'Region', value: 'Greater Seattle Area', type: FieldType.TEXT},
        {label: 'OPF Threshold Met', value: 'Abuse', type: FieldType.TEXT},
        {label: 'Lookback Period', value: '90 days', type: FieldType.TEXT},
        {label: 'BWNDs in 90 days lookback', value: '24', type: FieldType.NUMBER},
        {label: 'Blocks in 90 days lookback', value: '39', type: FieldType.NUMBER},
        {label: 'BWND Rate in 90 days lookback', value: '61.54 %', type: FieldType.TEXT},
        {label: 'OPF Probability Score', value: '0.0005', type: FieldType.NUMBER},
        {label: 'Business', value: 'SSD', type: FieldType.TEXT}
    ],

    history: {
        auditTrail: [
            {
                auditor: 'arjunaju',
                action: 'DECISION_OFFBOARD',
                timestamp: 1753004423000,
                notes: 'Primary auditor verdict: DP is eligible for offboarding after secondary review.',
                auditCheckResponses: {
                    'Is this a duplicate ticket?': 'No',
                    'Has this DP been previously reinstated?': 'No',
                    'Is the DP listed on the DND List?': 'No',
                    'Does the DP have an active High-Severity ticket?': 'No',
                    'Is DP still active?': 'Yes',
                    'Is the Offboarding ticket Fraud-related?': 'Yes'
                }
            },
            {
                auditor: 'anirumit',
                action: 'DECISION_OFFBOARD',
                timestamp: 1752989631000,
                notes: 'Offboard Reason: Overbooking Payment Fraud (OPF). Blurb: amazon_flex_sls_bwnd_termination. Investigation needed: No.',
                auditCheckResponses: {
                    'Is this a duplicate ticket?': 'No',
                    'Are there applicable SYSKAs on DPs account?': 'No',
                    'Is Offboarding ticket filled by authorized person?': 'Yes',
                    'Was DP alerted twice?': 'Yes',
                    'Is the Offboarding ticket Fraud-related?': 'Yes'
                }
            },
            {
                auditor: 'raunaksg',
                action: 'COMMENT',
                timestamp: 1752980000000,
                notes: 'Adding a remark for context — no decision taken.'
            }
        ],
        attachments: [
            {fileId: 'att-uuid-001', fileName: 'evidence-screenshot.png', fileSize: 329059, uploadedBy: 'arjunaju', uploadedAt: 1753004423000},
            {fileId: 'att-uuid-002', fileName: 'evidence-screenshot.png', fileSize: 329059, uploadedBy: 'anirumit', uploadedAt: 1752989631000}
        ]
    },

    decisions: ['NO_ACTION', 'OFFBOARDED'],
    additionalTabs: []
};
